import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, MotionConfig } from "framer-motion";
import { FaPhoneAlt, FaArrowRight } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import { IoMdMail } from "react-icons/io";
import { FiChevronDown, FiMessageCircle } from "react-icons/fi";
import SectionEyebrow from "../Components/SectionEyebrow";
import BrandButton from "../Components/BrandButton";

/* -------------------------------------------------------------------------- */
/*  Content          */
/* -------------------------------------------------------------------------- */

const EMAIL = "contact@mkaitechnology.com";
const PHONE_DISPLAY = "+1 737-343-8150";
const PHONE_HREF = "tel:+17373438150";

const OFFICES = [
  { country: "United States", address: "36 Paso Robles, Irvine, CA 82801" },
  { country: "United Kingdom", address: "Great Portland Street, London W1W 7NY" },
  { country: "Pakistan", address: "174 Spring North Phase 7, Bahria Town, Islamabad" },
];

const FAQS = [
  {
    q: "How do I get started?",
    a: `Send us a message with the form on this page, email ${EMAIL}, or call ${PHONE_DISPLAY}. Tell us about your project and our team will get back to you to arrange a free consultation.`,
  },
  {
    q: "What services do you offer?",
    a: "We build AI agents (conversational agents for voice, chat, and web), Twilio IVR systems, web apps, and mobile apps for Android and iOS. We also handle A2P 10DLC registration and approval.",
  },
  {
    q: "Can you help with Twilio IVR and A2P 10DLC approval?",
    a: "Yes. We implement IVR systems on Twilio with automated call routing and voice AI integration. For A2P, we handle the whole registration process, from brand verification to campaign submission, with message delivery through Twilio.",
  },
  {
    q: "Do you build web and mobile apps?",
    a: "Yes. We build responsive websites and full-stack web platforms, as well as Android and iOS applications tailored to your business needs.",
  },
  {
    q: "How long will my project take?",
    a: "It depends on the scope and complexity. Once you tell us about your project, we will share a clear estimate with you during the consultation.",
  },
  {
    q: "Where are you located?",
    a: "We have offices in Irvine (United States), London (United Kingdom), and Islamabad (Pakistan).",
  },
];

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// Same entrance animation the Home hero uses
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut", delay },
  }),
};


const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const sectionViewport = { once: false, amount: 0.18 };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldClass = (hasError) =>
  `w-full text-slate-900 placeholder-slate-400 bg-white text-[15px] rounded-lg border px-4 outline-none transition-[border-color,box-shadow] duration-200 focus:ring-4 ${
    hasError
      ? "border-red-400 hover:border-red-500 focus:border-red-500 focus:ring-red-100"
      : "border-slate-200 hover:border-[#8B4AFA]/50 focus:border-[#8B4AFA] focus:ring-[#8B4AFA]/15"
  }`;

const Field = ({ id, label, error, children, className = "mb-5", required = false }) => (
  <div className={className}>
    <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-700">
      {label}
      {required && (
        <span className="ml-0.5 text-red-500" aria-hidden="true">
          *
        </span>
      )}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} className="mt-1.5 text-xs text-red-500">
        {error}
      </p>
    )}
  </div>
);

const INITIAL = {
  firstName: "",
  lastName: "",
  contactNumber: "",
  email: "",
  message: "",
  smsConsent: false,
  website: "", // honeypot: real people never see or fill this
};

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

   // FAQ structured data (helps search results). Page title comes from Seo.js
   useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    });
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  const update = (name) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = "First name is required";
    if (!form.lastName.trim()) next.lastName = "Last name is required";

    const digits = form.contactNumber.replace(/\D/g, "");
    if (!form.contactNumber.trim()) next.contactNumber = "Contact number is required";
    else if (!/^[+\d\s().-]+$/.test(form.contactNumber) || digits.length < 7 || digits.length > 15)
      next.contactNumber = "Enter a valid phone number";

    if (!form.email.trim()) next.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address";

    if (!form.message.trim()) next.message = "Message is required";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      const first = ["firstName", "lastName", "contactNumber", "email", "message"].find(
        (key) => found[key]
      );
      document.getElementById(first)?.focus();
      return;
    }

    setSuccessMessage("");
    setErrorMessage("");

    // Bots fill the hidden field. Pretend success, send nothing.
    if (form.website) {
      setForm(INITIAL);
      setSuccessMessage("Thank you! Our team will get back to you shortly.");
      return;
    }

    setIsSubmitting(true);

    // The backend only accepts these 5 fields, so SMS consent
    // is added at the top of the message body.
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      contactNumber: form.contactNumber.trim(),
      email: form.email.trim(),
      message: [
        `SMS consent: ${form.smsConsent ? "Yes" : "No"}`,
        "",
        form.message.trim(),
      ].join("\n"),
    };

    try {
      // const response = await fetch("http://localhost:5820/send-mails", {
      const response = await fetch("https://backend.mkaitechnology.com/send-mails", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data.success === "true" || data.success === true) {
        setSuccessMessage(data.message || "Thank you! Our team will get back to you shortly.");
        setForm(INITIAL);
      } else {
        setErrorMessage(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Network Error Please Try Again Later");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = (e) => {
    e.preventDefault();
    document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <MotionConfig reducedMotion="user">
    <div className="mk-page relative overflow-x-clip text-slate-900">
      {/* ============================== HERO ============================== */}
    <section className="relative px-4 pb-12 pt-40 text-center sm:px-6 sm:pb-16 sm:pt-44">
      <div className="mx-auto max-w-3xl">
         <motion.p
          className="inline-flex items-center gap-2 rounded-md border border-[#8B4AFA]/20 bg-white/80 px-3 py-1.5 text-xs font-semibold uppercase text-[#6e3fc7]"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#8B4AFA]" />
          Free Consultation Available
        </motion.p>

          <motion.h1
              className="mt-6 font-display text-4xl font-semibold leading-[1.04] text-slate-900 sm:text-5xl lg:text-6xl"
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              Contact Our{" "}
            <span className="relative isolate inline-block px-1 text-[#6e3fc7]">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-[0.12em] -z-10 h-[0.3em] bg-[#E9D5FF]"
              />
              AI Experts
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-[#8B4AFA]"
              />
            </span>
          </motion.h1>

          <motion.p
            className="mx-auto mt-6 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg"
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
          >
            Have a project in mind? Whether it is an AI agent, a Twilio IVR, A2P approval, or a web
            or mobile app, tell us what you need and our team will get back to you shortly.
          </motion.p>

          {/* <motion.div
            className="mt-9 flex flex-col items-stretch justify-center gap-4 pb-1.5 pr-1.5 sm:flex-row sm:items-center"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.38}
          >
            <a
              href="#contact-form"
              onClick={scrollToForm}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-[#8B4AFA] px-6 py-3 text-sm font-semibold text-white shadow-[5px_5px_0_#6e3fc7] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#7c3aed] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8B4AFA] sm:w-auto"
            >
              Send a message <FaArrowRight aria-hidden="true" className="h-3 w-3" />
            </a>
            <a
              href={PHONE_HREF}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-md border border-[#8B4AFA] bg-white px-6 py-3 text-sm font-semibold text-[#6e3fc7] shadow-[5px_5px_0_#8B4AFA] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#F5F0FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8B4AFA] sm:w-auto"
            >
              <FaPhoneAlt aria-hidden="true" className="h-3 w-3" /> Call {PHONE_DISPLAY}
            </a>
          </motion.div> */}

        </div>
      </section>

      {/* =========================== CONTACT AREA ========================== */}
      <section className="relative z-10 pb-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            id="contact-form"
            className="relative scroll-mt-28 grid grid-cols-1 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-2xl shadow-slate-900/10 lg:grid-cols-5"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            custom={0}
          >
            <div className="absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r from-[#8B4AFA] to-[#bb94ff]" />
            {/* ---------------- Left: contact details ---------------- */}
           <aside className="order-2 flex flex-col border-t border-slate-200/70 bg-[#faf8ff] p-5 sm:p-8 lg:order-1 lg:col-span-2 lg:border-r lg:border-t-0 lg:p-10">
              <h2 className="font-display text-3xl font-semibold leading-9 text-slate-900">
                Contact us
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                We'd love to hear from you. Reach out anytime.
              </p>

              <ul className="mt-8 space-y-5">
                <li>
                  <a href={`mailto:${EMAIL}`} className="group/row flex items-center gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-[#8B4AFA] shadow-sm ring-1 ring-[#8B4AFA]/20">
                      <IoMdMail />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-slate-500">Email us</span>
                      <span className="block text-[13px] font-semibold text-slate-900 transition-colors [overflow-wrap:anywhere] group-hover/row:text-[#8B4AFA] min-[380px]:text-[15px]">
                    {EMAIL}
                  </span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href={PHONE_HREF} className="group/row flex items-center gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-sm text-[#8B4AFA] shadow-sm ring-1 ring-[#8B4AFA]/20">
                      <FaPhoneAlt />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-slate-500">Call us</span>
                      <span className="block text-[15px] font-semibold text-slate-900 transition-colors group-hover/row:text-[#8B4AFA]">
                        {PHONE_DISPLAY}
                      </span>
                    </span>
                  </a>
                </li>
              </ul>

              <div className="my-8 h-px bg-slate-200" />

              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <FaLocationDot className="text-[#8B4AFA]" />
                Our offices
              </p>
              <ul className="mt-4 space-y-4">
                {OFFICES.map((o) => (
                  <li key={o.country} className="border-l-2 border-[#8B4AFA]/30 pl-4 transition-colors hover:border-[#8B4AFA]">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        o.address
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B4AFA]"
                    >
                      <span className="block text-xs font-semibold uppercase tracking-wider text-[#8B4AFA]">
                        {o.country}
                      </span>
                      <span className="block text-sm leading-5 text-slate-700">{o.address}</span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-8 lg:mt-auto lg:pt-8">
                <div className="group relative aspect-[16/9] overflow-hidden rounded-xl shadow-md ring-1 ring-gray-900/10">
                  <img
                    src="/contact-image.webp"
                    alt="MK AI Technologies team"
                    width="700"
                    height="424"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
            </aside>

            {/* ---------------- Right: form card ---------------- */}
            <form
              onSubmit={handleSubmit}
              noValidate
              className="relative order-1 flex flex-col p-6 sm:p-8 lg:order-2 lg:col-span-3 lg:p-12"
            >
             <h2 className="mb-2 font-display text-3xl font-semibold leading-9 text-slate-900">Message us</h2>
              <p className="mb-8 text-sm text-slate-500">
                Fill out the form below and our team will get back to you shortly.
              </p>

              <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
               <Field id="firstName" label="First Name" error={errors.firstName} required>
                  <input
                    id="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={form.firstName}
                    onChange={update("firstName")}
                    placeholder="John"
                    aria-invalid={!!errors.firstName}
                    aria-describedby={errors.firstName ? "firstName-error" : undefined}
                    className={`h-12 ${fieldClass(errors.firstName)}`}
                  />
                </Field>
               <Field id="lastName" label="Last Name" error={errors.lastName} required>
                  <input
                    id="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={form.lastName}
                    onChange={update("lastName")}
                    placeholder="Doe"
                    aria-invalid={!!errors.lastName}
                    aria-describedby={errors.lastName ? "lastName-error" : undefined}
                    className={`h-12 ${fieldClass(errors.lastName)}`}
                  />
                </Field>
              </div>

             <Field id="contactNumber" label="Contact Number" error={errors.contactNumber} required>
                <input
                  id="contactNumber"
                  type="tel"
                  autoComplete="tel"
                  value={form.contactNumber}
                  onChange={update("contactNumber")}
                  placeholder="+1 (555) 000-0000"
                  aria-invalid={!!errors.contactNumber}
                  aria-describedby={errors.contactNumber ? "contactNumber-error" : undefined}
                  className={`h-12 ${fieldClass(errors.contactNumber)}`}
                />
              </Field>

              <Field id="email" label="Email" error={errors.email} required>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="john@example.com"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  className={`h-12 ${fieldClass(errors.email)}`}
                />
              </Field>

             <Field id="message" label="Message" error={errors.message} className="mb-5 flex flex-1 flex-col" required>
                <textarea
                  id="message"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Enter your message here"
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? "message-error" : undefined}
                  className={`min-h-[8rem] flex-1 resize-y pt-3 ${fieldClass(errors.message)}`}
                />
              </Field>

              {/* Honeypot: hidden from people and screen readers */}
              <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                />
              </div>

             <label className="mb-6 flex cursor-pointer items-start gap-3 rounded-lg bg-slate-50 p-4 transition-colors duration-200 hover:bg-[#8B4AFA]/5">
                <input
                  type="checkbox"
                  checked={form.smsConsent}
                  onChange={update("smsConsent")}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-[#8B4AFA]"
                />
                <span className="text-xs leading-relaxed text-slate-500">
                  I agree to receive reminder and updates SMS messages from "MK AI Technologies".
                  Message frequency may vary. Reply with "STOP" to unsubscribe. Standard message
                  and data rates may apply. This is optional. See our{" "}
                  <Link to="/privacy-policy" className="font-medium text-[#6e3fc7] underline transition-colors hover:text-[#8B4AFA]">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms-of-service" className="font-medium text-[#6e3fc7] underline transition-colors hover:text-[#8B4AFA]">
                    Terms of Service
                  </Link>
                  .
                </span>
              </label>

              <div aria-live="polite">
                {successMessage && (
                  <div className="mb-5 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5">
                    <p className="text-sm text-green-600">{successMessage}</p>
                  </div>
                )}
                {errorMessage && (
                  <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5">
                    <p className="text-sm text-red-600">{errorMessage}</p>
                  </div>
                )}
              </div>
              <BrandButton
                type="submit"
                disabled={isSubmitting}
                className="mk-cta-button--full mt-auto mb-1.5 mr-1.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    Sending...
                  </span>
                ) : (
                  "Send Message"
                )}
              </BrandButton>
            </form>
          </motion.div>
        </div>
      </section>

        {/* ================================ FAQ ============================== */}
       <section className="border-y border-slate-200/70 bg-[#FAF8FF] px-4 py-24 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-4xl">
            {/* Header */}
            <motion.div
              className="mx-auto max-w-3xl text-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={stagger}
            >
              <SectionEyebrow variants={reveal}>FAQ</SectionEyebrow>
              <motion.h2
                variants={reveal}
               className="mt-4 font-display text-3xl font-semibold !leading-[1.15] text-slate-900 [text-wrap:balance] lg:text-4xl"
              >
                Frequently Asked Questions
              </motion.h2>
              <motion.p
                variants={reveal}
                className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-600"
              >
                Quick answers to what people ask us most.
              </motion.p>
            </motion.div>

            {/* Accordion cards */}
            <div className="mt-12 space-y-3">
              {FAQS.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <motion.div
                    key={item.q}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    custom={i * 0.06}
                    className={`relative overflow-hidden rounded-2xl border bg-white transition-[border-color,box-shadow] duration-300 ${
                      isOpen
                        ? "border-[#8B4AFA] shadow-[0_12px_32px_-20px_rgba(15,23,42,0.3)]"
                        : "border-slate-200 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_6px_16px_-4px_rgba(15,23,42,0.03)] hover:border-[#8B4AFA]/40"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`absolute inset-x-0 top-0 h-1 bg-[#8B4AFA] transition-opacity duration-300 ${
                        isOpen ? "opacity-100" : "opacity-0"
                      }`}
                    />

                    <h3>
                      <button
                        type="button"
                        id={`faq-btn-${i}`}
                        aria-expanded={isOpen}
                        aria-controls={`faq-panel-${i}`}
                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                        className="group flex w-full items-center justify-between gap-4 px-5 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#8B4AFA] sm:px-7 sm:py-6"
                      >
                        <span className="flex min-w-0 items-center gap-4">
                          <span
                            aria-hidden="true"
                            className={`hidden shrink-0 rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 sm:inline-flex ${
                              isOpen ? "bg-[#8B4AFA] text-white" : "bg-[#8a4afa1c] text-[#6e3fc7]"
                            }`}
                          >
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="font-display text-base font-semibold text-slate-900 sm:text-lg">
                            {item.q}
                          </span>
                        </span>
                        <FiChevronDown
                          aria-hidden="true"
                          className={`h-5 w-5 shrink-0 transition-transform duration-300 motion-reduce:transition-none ${
                            isOpen
                              ? "rotate-180 text-[#8B4AFA]"
                              : "text-slate-400 group-hover:text-[#8B4AFA]"
                          }`}
                        />
                      </button>
                    </h3>

                    <div
                      id={`faq-panel-${i}`}
                      role="region"
                      aria-labelledby={`faq-btn-${i}`}
                      aria-hidden={!isOpen}
                      className={`grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none ${
                        isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="mx-5 border-t border-slate-100 pb-6 pt-4 text-sm leading-7 text-slate-600 sm:mx-7 sm:text-base">
                          {item.a}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
    </div>
    </MotionConfig>
  );
};

export default Contact;
